import React, { useEffect, useState } from 'react';
import { TextField, Button, Box, MenuItem, Paper, Typography, Divider } from '@mui/material';
import customAxios from 'routes/Pages/util/Api';
import authHeader from 'services/auth/jwt/auth-header';
import { useToast } from 'context/tostifyContext';
import { useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FETCH_ERROR, FETCH_START, FETCH_SUCCESS } from '../../../../@jumbo/constants/ActionTypes';
import { Checkbox, FormControlLabel } from '@mui/material';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material'; // /Users/Abhi/Desktop/Work/LogWatchReact/src/@jumbo/constants/ActionTypes.js
const AddUserForm = () => {
  const [form, setForm] = useState({
    username: '',
    password: '',
    displayName: '',
    roleName: '',
    defaultDriverUserName: '',
    defaultDriverDisplayName: '',
    isDriver: 0,
    isLoader: 0,
    isOperator: 0,
  });

  const [showPassword, setShowPassword] = useState(false);

  const [roles, setRoles] = useState([]);
  const { showToast } = useToast();

  const dispatch = useDispatch();
  const history = useHistory();

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await customAxios.get('/User/GetAllRoles', {
          headers: authHeader(),
        });
        setRoles(res.data.map(item => item.rolename));
      } catch (err) {
        console.error(err);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    const role = form.roleName?.toLowerCase().trim();

    setForm(prev => {
      let updated = {
        ...prev,
        isDriver: 0,
        isLoader: 0,
        isOperator: 0,
      };

      // ✅ Admin / Haulage Admin → all 3
      if (role === 'admin' || role === 'haulageadmin') {
        updated.isDriver = 1;
        updated.isLoader = 1;
        updated.isOperator = 1;
      }

      // ✅ Driver / Subcontractor*
      else if (role === 'driver' || role === 'subcontractor' || role?.startsWith('subcontractor')) {
        updated.isDriver = 1;
      }

      // ✅ Operator
      else if (role === 'operator') {
        updated.isOperator = 1;
      }

      return updated;
    });
  }, [form.roleName]);

  const handleChange = e => {
    const { name, value } = e.target;

    setForm(prev => {
      const updated = {
        ...prev,
        [name]: value,
      };

      // Auto-fill defaults
      if (name === 'username') {
        updated.defaultDriverUserName = value;
      }

      if (name === 'displayName') {
        updated.defaultDriverDisplayName = value;
      }

      return updated;
    });
  };

  const handleCheckboxChange = e => {
    const { name, checked } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: checked ? 1 : 0,
    }));
  };

  const handleSave = async () => {
    const isValidEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!isValidEmail(form.username)) {
      showToast('Please enter a valid email', 'error');
      return;
    }

    try {
      dispatch({ type: FETCH_START });

      const payload = {
        username: form.username,
        password: form.password,
        confirmPassword: form.password,
        displayName: form.displayName,
        roleName: form.roleName,
        defaultDriverUserName: form.defaultDriverUserName,
        defaultDriverDisplayName: form.defaultDriverDisplayName,
        isDriver: form.isDriver,
        isLoader: form.isLoader,
        isOperator: form.isOperator,
      };

      await customAxios.post('/User/Register', payload, {
        headers: authHeader(),
      });

      showToast('User registered successfully.');

      setForm({
        username: '',
        password: '',
        displayName: '',
        roleName: '',
        defaultDriverUserName: '',
        defaultDriverDisplayName: '',
        isDriver: '',
        isLoader: '',
        isOperator: '',
      });
      dispatch({ type: FETCH_SUCCESS });
    } catch (err) {
      console.error(err);
      showToast('Failed to add user.', 'error');
    }
  };

  return (
    <div className="app-wrapper siteWrapper">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <h2 style={{ fontSize: '1.5rem', margin: 0 }}>Add User</h2>
        <Box display="flex" alignItems="center" gap={3}>
          <Button variant="contained" color="primary" onClick={() => history.push('/')}>
            Home
          </Button>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: 4,
        }}>
        <Paper
          elevation={4}
          sx={{
            width: '100%',
            maxWidth: 600,
            p: 4,
            borderRadius: 3,
          }}>
          <Typography variant="h5" fontWeight="600" gutterBottom>
            Add User
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <Box display="flex" flexDirection="column" gap={3}>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Box flex={1} minWidth={250}>
                <TextField
                  label="Email"
                  name="username"
                  type="email"
                  value={form.username}
                  onChange={handleChange}
                  fullWidth
                  autoComplete="off"
                  required
                  error={!!form.username && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.username)}
                  helperText={
                    form.username && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.username) ? 'Enter a valid email address' : ''
                  }
                />
              </Box>
              <Box flex={1} minWidth={250}>
                <TextField
                  label="Display Name"
                  name="displayName"
                  value={form.displayName}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Box>
            </Box>
            <TextField
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={form.password}
              onChange={handleChange}
              fullWidth
              autoComplete="off" // 👈 important
              inputProps={{
                autoComplete: 'off',
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(prev => !prev)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              select
              label="Role"
              name="roleName"
              value={form.roleName}
              onChange={handleChange}
              fullWidth
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                    },
                  },
                },
              }}>
              {roles.map((role, i) => (
                <MenuItem key={i} value={role}>
                  {role}
                </MenuItem>
              ))}
            </TextField>

            <Box display="flex" gap={3} flexWrap="wrap">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isDriver === 1}
                    onChange={handleCheckboxChange}
                    name="isDriver"
                    disabled={
                      ['driver', 'subcontractor'].includes(form.roleName?.toLowerCase()) ||
                      form.roleName?.toLowerCase()?.startsWith('subcontractor') ||
                      ['admin', 'haulageadmin'].includes(form.roleName?.toLowerCase())
                    }
                  />
                }
                label="Is Driver"
              />

              <FormControlLabel
                control={<Checkbox checked={form.isLoader === 1} onChange={handleCheckboxChange} name="isLoader" />}
                label="Is Loader"
                disabled={['admin', 'haulageadmin'].includes(form.roleName?.toLowerCase())}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={form.isOperator === 1}
                    onChange={handleCheckboxChange}
                    name="isOperator"
                    disabled={
                      form.roleName?.toLowerCase() === 'operator' ||
                      ['admin', 'haulageadmin'].includes(form.roleName?.toLowerCase())
                    }
                  />
                }
                label="Is Operator"
              />
            </Box>

            <Box display="flex" gap={2} flexWrap="wrap">
              <Box flex={1} minWidth={250}>
                <TextField label="Default Driver Username" value={form.defaultDriverUserName} fullWidth disabled />
              </Box>

              <Box flex={1} minWidth={250}>
                <TextField label="Default Driver Display Name" value={form.defaultDriverDisplayName} fullWidth disabled />
              </Box>
            </Box>

            <Button
              variant="contained"
              size="large"
              onClick={handleSave}
              sx={{
                mt: 2,
                py: 1.2,
                fontWeight: '600',
              }}>
              Save User
            </Button>
          </Box>
        </Paper>
      </Box>
    </div>
  );
};

export default AddUserForm;
